import { Box, InputAdornment, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { Search } from "@mui/icons-material";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Task, TASK_STATUS } from "../../types/tasks";
import { User } from "../../types/users";
import { useUpdateTaskMutation } from "../../services/tasks.service";
import { RootState } from "../../store";
import TasksKanbanColumn from "./TasksKanbanColumn";
import TaskForm from "./TaskForm";

interface Props {
  tasks: Task[];
  users: User[];
}

type Filter = "all" | "mine";

function TasksKanbanBoard({ tasks, users }: Props) {
  const [updateTask] = useUpdateTaskMutation();
  const currentUser = useSelector((state: RootState) => state.user);
  const draggingId = useRef<string | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [editTask, setEditTask] = useState<Task | null>(null);

  const filteredTasks = tasks.filter((t) => {
    const q = search.toLowerCase();
    const matchesSearch = !q || t.name?.toLowerCase().includes(q) || t.refNo?.toLowerCase().includes(q);
    const matchesFilter =
      filter === "all" ||
      (filter === "mine" && (t.assignedTo === currentUser._id || t.createdBy === currentUser._id));
    return matchesSearch && matchesFilter;
  });

  const grouped = TASK_STATUS.reduce<Record<string, Task[]>>((acc, status) => {
    acc[status] = filteredTasks.filter((t) => t.status === status);
    return acc;
  }, {});

  const handleDrop = async (newStatus: string) => {
    if (!draggingId.current) return;
    const task = tasks.find((t) => t._id === draggingId.current);
    draggingId.current = null;
    if (!task || task.status === newStatus) return;
    await updateTask({ ...task, status: newStatus } as Task);
  };

  const handleEditSubmit = (task: Task) => {
    const cleaned = { ...task } as any;
    for (const key in cleaned) {
      if (cleaned[key] === "" || cleaned[key] === null) delete cleaned[key];
    }
    updateTask(cleaned as Task).then(() => setEditTask(null));
  };

  const overdueCount = filteredTasks.filter(
    (t) => t.status !== "Completed" && t.dueDate && new Date(t.dueDate as any) < new Date()
  ).length;

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ flexShrink: 0 }}>
        <TextField
          size="small"
          placeholder="Search tasks..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: "#94A3B8" }} />
              </InputAdornment>
            ),
          }}
        />

        <ToggleButtonGroup
          size="small"
          value={filter}
          exclusive
          onChange={(_, val) => val && setFilter(val)}
          sx={{ height: 36 }}
        >
          <ToggleButton value="all" sx={{ px: 1.5, fontSize: "0.72rem", fontWeight: 600, textTransform: "none" }}>
            All Tasks
          </ToggleButton>
          <ToggleButton value="mine" sx={{ px: 1.5, fontSize: "0.72rem", fontWeight: 600, textTransform: "none" }}>
            My Tasks
          </ToggleButton>
        </ToggleButtonGroup>

        <Stack direction="row" spacing={2} sx={{ ml: "auto !important" }}>
          <Typography variant="body2" color="text.secondary">
            <b>{filteredTasks.length}</b> tasks
          </Typography>
          {overdueCount > 0 && (
            <Typography variant="body2" color="text.secondary">
              <b style={{ color: "#EF4444" }}>{overdueCount}</b> overdue
            </Typography>
          )}
        </Stack>
      </Stack>

      <Box
        sx={{
          flex: 1,
          overflowX: "auto",
          overflowY: "hidden",
          "&::-webkit-scrollbar": { height: 6 },
          "&::-webkit-scrollbar-track": { bgcolor: "#F1F5F9", borderRadius: 999 },
          "&::-webkit-scrollbar-thumb": { bgcolor: "#CBD5E1", borderRadius: 999 },
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ minWidth: "max-content", height: "100%", px: 0.5, py: 0.25 }}
        >
          {TASK_STATUS.map((status) => (
            <TasksKanbanColumn
              key={status}
              status={status}
              tasks={grouped[status] ?? []}
              users={users}
              onDragStart={(id) => { draggingId.current = id; }}
              onDrop={handleDrop}
              onCardClick={setEditTask}
            />
          ))}
        </Stack>
      </Box>

      {editTask && (
        <TaskForm
          open={!!editTask}
          edit
          selectedTask={editTask}
          users={users}
          handleClose={() => setEditTask(null)}
          submitHanlder={handleEditSubmit}
        />
      )}
    </Box>
  );
}

export default TasksKanbanBoard;
